import React, { useState, useEffect, useRef, useContext, createContext } from 'react';

// ── Context ───────────────────────────────────────────────────────────────────

export const FilterContext = createContext(null);

// ── Market hierarchy data ─────────────────────────────────────────────────────

const STATE_CBSAS = {
  // Northeast
  'Connecticut':     ['Hartford', 'New Haven', 'Bridgeport', 'Waterbury'],
  'Massachusetts':   ['Boston-Cambridge', 'Worcester', 'Springfield', 'Barnstable'],
  'Maine':           ['Portland-South Portland', 'Bangor', 'Lewiston-Auburn'],
  'New Hampshire':   ['Manchester', 'Nashua', 'Dover-Rochester'],
  'New Jersey':      ['Newark', 'Trenton', 'Atlantic City', 'Ocean City'],
  'New York':        ['New York City', 'Buffalo', 'Rochester', 'Albany'],
  'Pennsylvania':    ['Philadelphia', 'Pittsburgh', 'Allentown', 'Harrisburg'],
  'Rhode Island':    ['Providence', 'Warwick', 'Newport'],
  'Vermont':         ['Burlington', 'Rutland', 'Barre'],
  // Southeast
  'Alabama':         ['Birmingham', 'Huntsville', 'Mobile', 'Montgomery'],
  'Arkansas':        ['Little Rock', 'Fayetteville', 'Fort Smith', 'Jonesboro'],
  'Florida':         ['Miami', 'Orlando', 'Tampa', 'Jacksonville'],
  'Georgia':         ['Atlanta', 'Savannah', 'Augusta', 'Columbus'],
  'Kentucky':        ['Louisville', 'Lexington', 'Bowling Green'],
  'Louisiana':       ['New Orleans', 'Baton Rouge', 'Lafayette', 'Shreveport'],
  'Mississippi':     ['Jackson', 'Gulfport-Biloxi', 'Hattiesburg'],
  'North Carolina':  ['Charlotte', 'Raleigh', 'Greensboro', 'Durham'],
  'South Carolina':  ['Columbia', 'Charleston', 'Greenville', 'Myrtle Beach'],
  'Tennessee':       ['Nashville', 'Memphis', 'Knoxville', 'Chattanooga'],
  'Virginia':        ['Virginia Beach', 'Richmond', 'Northern Virginia', 'Roanoke'],
  'West Virginia':   ['Charleston', 'Huntington', 'Morgantown'],
  // Midwest
  'Iowa':            ['Des Moines', 'Cedar Rapids', 'Davenport'],
  'Illinois':        ['Chicago', 'Rockford', 'Peoria', 'Springfield'],
  'Indiana':         ['Indianapolis', 'Fort Wayne', 'South Bend', 'Evansville'],
  'Kansas':          ['Wichita', 'Kansas City', 'Topeka'],
  'Michigan':        ['Detroit', 'Grand Rapids', 'Lansing', 'Flint'],
  'Minnesota':       ['Minneapolis-St. Paul', 'Rochester', 'Duluth', 'St. Cloud'],
  'Missouri':        ['St. Louis', 'Kansas City', 'Springfield'],
  'North Dakota':    ['Fargo', 'Bismarck', 'Grand Forks'],
  'Nebraska':        ['Omaha', 'Lincoln', 'Grand Island'],
  'Ohio':            ['Columbus', 'Cleveland', 'Cincinnati', 'Akron'],
  'South Dakota':    ['Sioux Falls', 'Rapid City'],
  'Wisconsin':       ['Milwaukee', 'Madison', 'Green Bay', 'Racine'],
  // West
  'Alaska':          ['Anchorage', 'Fairbanks'],
  'Arizona':         ['Phoenix', 'Tucson', 'Mesa', 'Flagstaff'],
  'California':      ['Los Angeles', 'San Francisco', 'San Diego', 'Sacramento'],
  'Colorado':        ['Denver', 'Colorado Springs', 'Boulder', 'Fort Collins'],
  'Hawaii':          ['Honolulu', 'Kahului-Wailuku'],
  'Idaho':           ['Boise City', 'Nampa', 'Idaho Falls'],
  'Montana':         ['Billings', 'Missoula', 'Great Falls'],
  'New Mexico':      ['Albuquerque', 'Santa Fe', 'Las Cruces'],
  'Nevada':          ['Las Vegas', 'Reno', 'Carson City'],
  'Oregon':          ['Portland', 'Salem', 'Eugene', 'Bend'],
  'Utah':            ['Salt Lake City', 'Provo', 'Ogden', 'St. George'],
  'Washington':      ['Seattle', 'Spokane', 'Tacoma', 'Bellingham'],
  'Wyoming':         ['Cheyenne', 'Casper'],
};

const REGIONS = {
  'Northeast': ['Connecticut','Massachusetts','Maine','New Hampshire','New Jersey','New York','Pennsylvania','Rhode Island','Vermont'],
  'Southeast': ['Alabama','Arkansas','Florida','Georgia','Kentucky','Louisiana','Mississippi','North Carolina','South Carolina','Tennessee','Virginia','West Virginia'],
  'Midwest':   ['Iowa','Illinois','Indiana','Kansas','Michigan','Minnesota','Missouri','North Dakota','Nebraska','Ohio','South Dakota','Wisconsin'],
  'West':      ['Alaska','Arizona','California','Colorado','Hawaii','Idaho','Montana','New Mexico','Nevada','Oregon','Utah','Washington','Wyoming'],
};

function buildTowns(cbsa) {
  return [`${cbsa} — Downtown`, `${cbsa} — Suburbs`, `${cbsa} — Outskirts`];
}

function buildRatingRegions(state, cbsas) {
  const mid = Math.ceil(cbsas.length / 2);
  const makeCBSAs = list => list.map(c => ({ name: c, towns: buildTowns(c) }));
  return [
    { name: `${state} — North`, cbsas: makeCBSAs(cbsas.slice(0, mid)) },
    { name: `${state} — South`, cbsas: makeCBSAs(cbsas.slice(mid)) },
    { name: `${state} — Metro`, cbsas: makeCBSAs(cbsas.slice(0, 1)) },
  ];
}

const MARKET_HIERARCHY = {
  regions: Object.entries(REGIONS).map(([region, states]) => ({
    name: region,
    states: states.map(s => ({
      name: s,
      ratingRegions: buildRatingRegions(s, STATE_CBSAS[s] || []),
    })),
  })),
};

// ── Filter option lists ───────────────────────────────────────────────────────

const SUB_GROUPS = {
  'Cardiology':        ['CHF', 'Atrial Fibrillation', 'Coronary Artery Disease', 'Hypertension'],
  'MSK':               ['Hip Replacement', 'Knee Replacement', 'Spine Surgery', 'Physical Therapy'],
  'Oncology':          ['Breast Cancer', 'Lung Cancer', 'Colorectal Cancer', 'Hematologic Malignancy'],
  'Behavioral Health': ['Depression', 'Anxiety', 'Substance Use Disorder', 'Schizophrenia'],
  'Primary Care':      ['Preventive Care', 'Chronic Disease Management', 'Acute Care', 'Annual Wellness'],
  'Endocrinology':     ['Type 2 Diabetes', 'Type 1 Diabetes', 'Thyroid Disorders', 'Obesity'],
  'Nephrology':        ['CKD Stage 3', 'CKD Stage 4', 'CKD Stage 5', 'ESRD'],
  'Pulmonology':       ['COPD', 'Asthma', 'Sleep Apnea', 'Pulmonary Hypertension'],
  'Neurology':         ['Stroke', 'Epilepsy', 'Multiple Sclerosis', "Parkinson's Disease"],
  'Gastroenterology':  ['IBD', 'IBS', 'GERD', 'Colorectal Screening'],
};

const OPTIONS = {
  lob:                ['Commercial (COM)', 'ASO', 'CARE', 'Medicaid (CAID)'],
  product:            ['Large Group Commercial', 'Small Group Commercial', 'Individual Commercial', 'Individual Medicare Advantage', 'Group Medicare Advantage', 'Individual Medicaid'],
  product2:           ['HIX', 'Non-HIX', 'UMPW', 'Dual Eligibles', 'Non-Dual Eligibles'],
  gender:             ['Male', 'Female', 'Other'],
  ageBand:            ['0–18', '19–30', '31–40', '41–50', '51–60', '61–64', '65+'],
  enrollmentDuration: ['< 6 months', '6–12 months', '12–24 months', '24+ months'],
  dateRange:          ['Incurred & Paid Through', 'Rolling 3 Months', 'Rolling 6 Months', 'Rolling 9 Months', 'Rolling 12 Months', 'Rolling 24 Months', 'Calendar Year', 'Quarterly', 'Monthly'],
  specialty:          ['Specialty', 'Non-Specialty'],
  specialtyCategory:  Object.keys(SUB_GROUPS),
};

// ── Default state (exported so DashboardPage can initialise) ──────────────────

export const DEFAULT_FILTERS = {
  lob: [],
  product: [],
  product2: [],
  market: { path: [] },
  gender: [],
  ageBand: [],
  enrollmentDuration: [],
  dateRange: '',
  riskAdjusted: false,
  ibnr: false,
  attributedPcpGroup: '',
  attributedSpecGroup: '',
  vbpProgramId: '',
  specialtyCategory: [],
  specialty: '',
  specialtySubGroup: [],
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function availableSubGroups(selectedCategories) {
  if (!selectedCategories.length) return Object.values(SUB_GROUPS).flat();
  return selectedCategories.flatMap(cat => SUB_GROUPS[cat] || []);
}

export function activeChips(filters) {
  const c = [];
  if (filters.lob.length)                c.push({ key: 'lob',                label: `LOB: ${filters.lob.join(', ')}` });
  if (filters.product.length)            c.push({ key: 'product',            label: `Product: ${filters.product.length} selected` });
  if (filters.product2.length)           c.push({ key: 'product2',           label: `Product 2: ${filters.product2.length} selected` });
  if (filters.market.path[0])            c.push({ key: 'market',             label: `Market: ${filters.market.path[filters.market.path.length - 1]}` });
  if (filters.gender.length)             c.push({ key: 'gender',             label: `Gender: ${filters.gender.join(', ')}` });
  if (filters.ageBand.length)            c.push({ key: 'ageBand',            label: `Age: ${filters.ageBand.length} selected` });
  if (filters.enrollmentDuration.length) c.push({ key: 'enrollmentDuration', label: `Enrollment: ${filters.enrollmentDuration.length} selected` });
  if (filters.dateRange)                 c.push({ key: 'dateRange',          label: `Date: ${filters.dateRange}` });
  if (filters.riskAdjusted)              c.push({ key: 'riskAdjusted',       label: 'Risk Adjusted: Yes' });
  if (filters.ibnr)                      c.push({ key: 'ibnr',               label: 'IBNR: With' });
  if (filters.attributedPcpGroup)        c.push({ key: 'attributedPcpGroup', label: `PCP: ${filters.attributedPcpGroup}` });
  if (filters.attributedSpecGroup)       c.push({ key: 'attributedSpecGroup',label: `Spec: ${filters.attributedSpecGroup}` });
  if (filters.vbpProgramId)             c.push({ key: 'vbpProgramId',       label: `VBP: ${filters.vbpProgramId}` });
  if (filters.specialtyCategory.length)  c.push({ key: 'specialtyCategory',  label: `Category: ${filters.specialtyCategory.join(', ')}` });
  if (filters.specialty)                 c.push({ key: 'specialty',          label: `Specialty: ${filters.specialty}` });
  if (filters.specialtySubGroup.length)  c.push({ key: 'specialtySubGroup',  label: `Sub-Group: ${filters.specialtySubGroup.length} selected` });
  return c;
}

// ── MultiSelect ───────────────────────────────────────────────────────────────

function MultiSelect({ options, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = opt => onChange(value.includes(opt) ? value.filter(v => v !== opt) : [...value, opt]);
  const label  = value.length === 0 ? placeholder : value.length === 1 ? value[0] : `${value.length} selected`;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type='button' className='filter-multiselect-btn form-control form-control-sm' onClick={() => setOpen(o => !o)}>
        <span style={{ color: value.length === 0 ? 'var(--eh-text-mid)' : 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <span style={{ flexShrink: 0, color: 'var(--eh-text-mid)', marginLeft: '0.25rem' }}>▾</span>
      </button>
      {open && (
        <div className='filter-multiselect-dropdown'>
          {value.length > 0 && (
            <div className='px-2 pt-1 pb-0'>
              <button type='button' className='btn btn-link btn-sm p-0' style={{ fontSize: '0.72rem' }} onClick={() => onChange([])}>Clear all</button>
            </div>
          )}
          {options.map(opt => (
            <label key={opt} className='filter-multiselect-option'>
              <input type='checkbox' checked={value.includes(opt)} onChange={() => toggle(opt)} />
              {opt}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ── CascadingMarket ───────────────────────────────────────────────────────────

const DEPTH_LABELS  = ['Region', 'State', 'Rating Region', 'CBSA', 'Town'];

function getOptionsAtDepth(path, depthIdx) {
  if (depthIdx === 0) return MARKET_HIERARCHY.regions.map(r => r.name);
  const region = MARKET_HIERARCHY.regions.find(r => r.name === path[0]);
  if (depthIdx === 1) return region ? region.states.map(s => s.name) : [];
  const state = region?.states.find(s => s.name === path[1]);
  if (depthIdx === 2) return state ? state.ratingRegions.map(r => r.name) : [];
  const rr = state?.ratingRegions.find(r => r.name === path[2]);
  if (depthIdx === 3) return rr ? rr.cbsas.map(c => c.name) : [];
  const cbsa = rr?.cbsas.find(c => c.name === path[3]);
  if (depthIdx === 4) return cbsa ? cbsa.towns : [];
  return [];
}

function CascadingMarket({ value, onChange }) {
  const { path } = value;
  const visibleDepths = Math.min(path.length + 1, DEPTH_LABELS.length);

  const handleSelect = (i, val) => {
    const newPath = val ? [...path.slice(0, i), val] : path.slice(0, i);
    onChange({ path: newPath });
  };

  return (
    <div className='d-flex flex-wrap gap-2'>
      {Array.from({ length: visibleDepths }).map((_, i) => (
        <select
          key={i}
          className='form-select form-select-sm'
          style={{ minWidth: 140, flex: 1 }}
          value={path[i] || ''}
          onChange={e => handleSelect(i, e.target.value)}
        >
          <option value=''>— {DEPTH_LABELS[i]} —</option>
          {getOptionsAtDepth(path, i).map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      ))}
    </div>
  );
}

// ── FilterGroup ───────────────────────────────────────────────────────────────

function FilterGroup({ label, children }) {
  return (
    <div>
      <div className='filter-group-label'>{label}</div>
      {children}
    </div>
  );
}

function FieldLabel({ children }) {
  return <label className='filter-field-label'>{children}</label>;
}

// ── FilterShelf ───────────────────────────────────────────────────────────────

function PopulationGroup({ filters, onField }) {
  return (
    <FilterGroup label='Population'>
      <div className='row g-1'>
        <div className='col-6'><FieldLabel>LOB</FieldLabel><MultiSelect options={OPTIONS.lob} value={filters.lob} onChange={v => onField('lob', v)} placeholder='All' /></div>
        <div className='col-6'><FieldLabel>Product</FieldLabel><MultiSelect options={OPTIONS.product} value={filters.product} onChange={v => onField('product', v)} placeholder='All' /></div>
        <div className='col-6'><FieldLabel>Product 2</FieldLabel><MultiSelect options={OPTIONS.product2} value={filters.product2} onChange={v => onField('product2', v)} placeholder='All' /></div>
        <div className='col-6'><FieldLabel>Gender</FieldLabel><MultiSelect options={OPTIONS.gender} value={filters.gender} onChange={v => onField('gender', v)} placeholder='All' /></div>
        <div className='col-6'><FieldLabel>Age Band</FieldLabel><MultiSelect options={OPTIONS.ageBand} value={filters.ageBand} onChange={v => onField('ageBand', v)} placeholder='All' /></div>
        <div className='col-6'><FieldLabel>Enrollment Duration</FieldLabel><MultiSelect options={OPTIONS.enrollmentDuration} value={filters.enrollmentDuration} onChange={v => onField('enrollmentDuration', v)} placeholder='All' /></div>
        <div className='col-12'><FieldLabel>Market</FieldLabel><CascadingMarket value={filters.market} onChange={v => onField('market', v)} /></div>
      </div>
    </FilterGroup>
  );
}

function MeasurementGroup({ filters, onField }) {
  return (
    <FilterGroup label='Measurement'>
      <div className='d-flex flex-column gap-1'>
        <div>
          <FieldLabel>Date Range</FieldLabel>
          <select className='form-select form-select-sm' value={filters.dateRange} onChange={e => onField('dateRange', e.target.value)}>
            <option value=''>All</option>
            {OPTIONS.dateRange.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <label className='filter-check-label'>
          <input type='checkbox' checked={filters.riskAdjusted} onChange={e => onField('riskAdjusted', e.target.checked)} />
          Risk Adjusted
        </label>
        <label className='filter-check-label'>
          <input type='checkbox' checked={filters.ibnr} onChange={e => onField('ibnr', e.target.checked)} />
          Include IBNR
        </label>
      </div>
    </FilterGroup>
  );
}

function AttributionGroup({ filters, onField }) {
  return (
    <FilterGroup label='Attribution'>
      <div className='d-flex flex-column gap-1'>
        <div>
          <FieldLabel>Attributed PCP Group (TIN / NPI)</FieldLabel>
          <input className='form-control form-control-sm' value={filters.attributedPcpGroup} onChange={e => onField('attributedPcpGroup', e.target.value)} placeholder='Enter TIN or NPI' />
        </div>
        <div>
          <FieldLabel>Attributed Spec Group (TIN / NPI)</FieldLabel>
          <input className='form-control form-control-sm' value={filters.attributedSpecGroup} onChange={e => onField('attributedSpecGroup', e.target.value)} placeholder='Enter TIN or NPI' />
        </div>
        <div>
          <FieldLabel>VBP Program ID</FieldLabel>
          <input className='form-control form-control-sm' value={filters.vbpProgramId} onChange={e => onField('vbpProgramId', e.target.value)} placeholder='Enter Program ID' />
        </div>
      </div>
    </FilterGroup>
  );
}

function ClinicalGroup({ filters, onField, onChange }) {
  const subGroupOptions = availableSubGroups(filters.specialtyCategory);

  const handleCategory = cats => {
    const valid = new Set(availableSubGroups(cats));
    onChange({ ...filters, specialtyCategory: cats, specialtySubGroup: filters.specialtySubGroup.filter(s => valid.has(s)) });
  };

  return (
    <FilterGroup label='Clinical'>
      <div className='d-flex flex-column gap-1'>
        <div>
          <FieldLabel>Specialty</FieldLabel>
          <select className='form-select form-select-sm' value={filters.specialty} onChange={e => onField('specialty', e.target.value)}>
            <option value=''>All</option>
            {OPTIONS.specialty.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <FieldLabel>Specialty Category</FieldLabel>
          <MultiSelect options={OPTIONS.specialtyCategory} value={filters.specialtyCategory} onChange={handleCategory} placeholder='All' />
        </div>
        <div>
          <FieldLabel>Specialty Sub-Group</FieldLabel>
          <MultiSelect options={subGroupOptions} value={filters.specialtySubGroup} onChange={v => onField('specialtySubGroup', v)} placeholder='All' />
        </div>
      </div>
    </FilterGroup>
  );
}

// ── FilterPanel ───────────────────────────────────────────────────────────────

export function FilterPanel() {
  const { filters, onChange, setOpen } = useContext(FilterContext);
  const onField   = (key, val) => onChange({ ...filters, [key]: val });
  const hasActive = activeChips(filters).length > 0;
  const reset     = () => onChange({ ...DEFAULT_FILTERS });

  return (
    <div className='filter-shelf-panel'>
      <div className='d-flex justify-content-between align-items-center mb-2'>
        <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--eh-navy)' }}>Filters</span>
        <div className='d-flex gap-3'>
          {hasActive && <button type='button' className='btn btn-link btn-sm p-0' style={{ fontSize: '0.75rem' }} onClick={reset}>Reset All</button>}
          <button type='button' className='btn btn-link btn-sm p-0' style={{ fontSize: '0.75rem' }} onClick={() => setOpen(false)}>✕ Close</button>
        </div>
      </div>
      <div className='row g-3'>
        <div className='col-12 col-lg-4'><PopulationGroup filters={filters} onField={onField} /></div>
        <div className='col-12 col-sm-6 col-lg-2'><MeasurementGroup filters={filters} onField={onField} /></div>
        <div className='col-12 col-sm-6 col-lg-3'><AttributionGroup filters={filters} onField={onField} /></div>
        <div className='col-12 col-lg-3'><ClinicalGroup filters={filters} onField={onField} onChange={onChange} /></div>
      </div>
    </div>
  );
}

// ── FilterProvider ────────────────────────────────────────────────────────────

export function FilterProvider({ children }) {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [open, setOpen]       = useState(false);
  return (
    <FilterContext.Provider value={{ filters, onChange: setFilters, open, setOpen }}>
      {children}
    </FilterContext.Provider>
  );
}
